import Sparkline from "components/company/sections/summary/adornments/Sparkline";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import { compareAsc, getYear } from "date-fns";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { palette } from "lib/miolo";
import parseDateYmd from "lib/utils/parseDateYmd";
interface IDebtObligationsSummaryProps {
  debtObligations: IBrazilianCompanyIssuerTopLevelFragment["debtObligations"];
}

export default function DebtObligationsSummary({ debtObligations }: IDebtObligationsSummaryProps) {
  if (debtObligations.length < 2) {
    return null;
  }

  const obligations = debtObligations
    .map((obligation) => {
      return {
        ...obligation,
        fiscalYearStart: parseDateYmd(obligation.fiscalYearStart),
        fiscalYearEnd: parseDateYmd(obligation.fiscalYearEnd),
      };
    })
    .sort((a, b) => {
      return compareAsc(a.fiscalYearStart, b.fiscalYearStart);
    });

  const currentObligation = obligations[obligations.length - 1];
  const previousObligation = obligations[obligations.length - 2];
  const differenceInPercent = Math.ceil((currentObligation.totalDebt / previousObligation.totalDebt - 1) * 100);
  const isDown = currentObligation.totalDebt < previousObligation.totalDebt;
  const primaryColor = isDown ? palette.viridian : palette.mojo;
  const previousFiscalYear = getYear(previousObligation.fiscalYearEnd);
  const currentFiscalYear = getYear(currentObligation.fiscalYearStart);
  const variationSign = differenceInPercent < 0 ? "-" : "+";
  const tooltipTextContent = `Variação de ${variationSign}${Math.abs(
    differenceInPercent,
  )}% de ${previousFiscalYear} para ${currentFiscalYear}`;

  if (!currentObligation && !previousObligation) {
    return null;
  }

  return (
    <div className="flex items-center justify-between xl:justify-start xl:space-x-6">
      <div>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild={true}>
              <h6 className="text-xl font-bold text-red-600">
                {variationSign}
                {Math.abs(differenceInPercent)}%
              </h6>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              sideOffset={12}
              className="max-w-xs rounded-xl border border-transparent bg-black text-white"
            >
              {tooltipTextContent}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <span className="text-sm font-medium text-gray-500">
          {currentFiscalYear} vs {previousFiscalYear}
        </span>
      </div>

      {debtObligations.length >= 3 && (
        <Sparkline
          ariaLabel="Gráfico demonstrando a evolução do total da Dívida"
          nodes={debtObligations.map((node) => node.totalDebt)}
          chart={{
            margin: { top: 0, left: 0, bottom: 0, right: 0 },
            padding: { top: 4, left: 4, bottom: 4, right: 4 },
            primaryColor,
            secondaryColor: isDown ? palette.whiteIce : palette.vanillaIce,
            width: 180,
            height: 40,
          }}
        />
      )}
    </div>
  );
}
